import React, { useEffect, useState } from 'react';
import { Card, Table, Form, Input, Button, Space, Badge, Typography, Popconfirm, message, Modal, Select } from 'antd';
import { useTableRequest } from '@/hooks';
import {
  getCustomerProviderBindList,
  unbindCustomerProvider,
  bindCustomerProvider,
  CustomerProviderBindResponse,
} from '@/api/customer';
import { getCustomerUsers, getProviderUsers } from '@/api/workorder';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';

const { Text } = Typography;

const statusMap: Record<number, { color: string; label: string }> = {
  1: { color: 'green', label: '绑定中' },
  0: { color: 'gray', label: '已解绑' },
};

const CustomerProviderPage: React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [bindModalOpen, setBindModalOpen] = useState(false);

  const { loading, dataSource, pagination, form, reload, search, reset } = useTableRequest<CustomerProviderBindResponse>({
    onRequest: (params) => {
      return getCustomerProviderBindList(params);
    },
  });

  const { data: customerData, refresh: refreshCustomers } = useRequest(getCustomerUsers, { manual: true });
  const { data: providerData, refresh: refreshProviders } = useRequest(getProviderUsers, { manual: true });

  useEffect(() => {
    if (bindModalOpen) {
      refreshCustomers();
      refreshProviders();
    }
  }, [bindModalOpen]);

  const handleUnbind = async (record: CustomerProviderBindResponse) => {
    const resp = await unbindCustomerProvider(record.customerId, record.providerId);
    if (resp.success) {
      messageApi.success('解绑成功');
      reload();
    } else {
      messageApi.error(resp.errorMsg || '解绑失败');
    }
  };

  const handleBindOk = async (values: { customerId: string; providerId: string }) => {
    const resp = await bindCustomerProvider({ customerId: values.customerId, providerId: values.providerId });
    if (resp.success) {
      messageApi.success('绑定成功');
      setBindModalOpen(false);
      reload();
    } else {
      messageApi.error(resp.errorMsg || '绑定失败');
    }
  };

  return (
    <>
      {contextHolder}
      <Form layout="inline" className="ml20 mt20" form={form}>
        <Form.Item key="keyword" name="keyword">
          <Input placeholder="搜索客户/服务商/设备" style={{ width: 200 }} allowClear />
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={search}>查询</Button>
        </Form.Item>
        <Form.Item>
          <Button type="default" onClick={reset}>重置</Button>
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={() => setBindModalOpen(true)}>绑定客户与服务商</Button>
        </Form.Item>
      </Form>

      <Card className="m20" size="small">
        <Table
          loading={loading}
          dataSource={dataSource}
          pagination={pagination}
          rowKey="id"
          size="small"
          columns={[
            {
              title: '客户',
              key: 'customer',
              dataIndex: 'customer',
              width: 120,
              ellipsis: true,
            },
            {
              title: '服务商',
              key: 'provider',
              dataIndex: 'provider',
              width: 120,
              render: (value) => value || '-',
            },
            {
              title: '联系电话',
              key: 'phone',
              dataIndex: 'phone',
              width: 130,
              render: (value) => value || '-',
            },
            {
              title: '关联设备',
              key: 'deviceName',
              dataIndex: 'deviceName',
              width: 150,
              render: (value) => value || '-',
            },
            {
              title: '绑定时间',
              key: 'bindTime',
              dataIndex: 'bindTime',
              width: 170,
              render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-'),
            },
            {
              title: '状态',
              key: 'status',
              dataIndex: 'status',
              width: 80,
              render: (value) => {
                const s = statusMap[value];
                return s ? (
                  <>
                    <Badge color={s.color} />
                    <Text className="ml5">{s.label}</Text>
                  </>
                ) : '-';
              },
            },
            {
              title: '操作',
              key: 'action',
              width: 80,
              render: (_, record) =>
                record.status === 1 ? (
                  <Popconfirm
                    title={`确定解绑 ${record.customer} 与 ${record.provider} 的绑定关系？`}
                    description="解绑后将移除该客户在该服务商下的所有设备绑定"
                    okText="确定"
                    cancelText="取消"
                    onConfirm={() => handleUnbind(record)}
                  >
                    <Typography.Link type="danger">解绑</Typography.Link>
                  </Popconfirm>
                ) : null,
            },
          ]}
        />
      </Card>

      {/* 绑定弹窗 */}
      <Modal
        title="绑定客户与服务商"
        open={bindModalOpen}
        onCancel={() => setBindModalOpen(false)}
        destroyOnHidden
        footer={null}
      >
        <Form layout="vertical" onFinish={handleBindOk}>
          <Form.Item
            label="选择客户"
            name="customerId"
            rules={[{ required: true, message: '请选择客户' }]}
          >
            <Select
              placeholder="请选择客户"
              options={(customerData?.result || []).map((c: any) => ({ value: c.id, label: c.nickName || c.username }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item
            label="选择服务商"
            name="providerId"
            rules={[{ required: true, message: '请选择服务商' }]}
          >
            <Select
              placeholder="请选择服务商"
              options={(providerData?.result || []).map((p: any) => ({ value: p.id, label: p.nickName || p.username }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setBindModalOpen(false)}>取消</Button>
              <Button type="primary" htmlType="submit">确定绑定</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CustomerProviderPage;
